import { useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook to synchronize table state (pagination, sorting, filtering, search) with URL parameters.
 * @param {Object} initialState - Default state values
 * @returns {Object} - { tableState, setTableState, onPaginationChange, onSortingChange, onGlobalFilterChange, onColumnFiltersChange }
 */
const useTableUrlState = (initialState = {}) => {
    const { persistenceKey } = initialState;
    const [searchParams, setSearchParams] = useSearchParams();
    const restorationAttempted = useRef(false);

    // 1. Restore from storage on mount if URL is empty
    useEffect(() => {
        if (!persistenceKey || restorationAttempted.current) return;

        const hasTableParams = searchParams.has('page') || 
                               searchParams.has('filters') || 
                               searchParams.has('search') || 
                               searchParams.has('sort');

        if (!hasTableParams) {
            const saved = localStorage.getItem(`table_state_${persistenceKey}`);
            if (saved) {
                try {
                    setSearchParams(new URLSearchParams(saved), { replace: true });
                } catch (e) {
                    console.warn(`Failed to restore table state for ${persistenceKey}`, e);
                }
            }
        }
        restorationAttempted.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only on mount

    // 2. Persist to storage whenever URL params change
    useEffect(() => {
        if (!persistenceKey || !restorationAttempted.current) return;
        
        const current = searchParams.toString();
        // Store current string (including empty) to ensure reset filters are persisted
        localStorage.setItem(`table_state_${persistenceKey}`, current);
    }, [searchParams, persistenceKey]);

    // Parse initial state from URL or use defaults
    const parsedState = useMemo(() => {
        const pageVal = searchParams.get('page');
        const limitVal = searchParams.get('limit');
        
        // Internal state is 0-indexed, URL is 1-indexed
        const page = pageVal ? Math.max(0, parseInt(pageVal, 10) - 1) : 0;
        const size = parseInt(limitVal || initialState.pageSize || '10', 10);
        const search = searchParams.get('search') || '';

        let sorting = [];
        try {
            const sortParam = searchParams.get('sort');
            if (sortParam) {
                sorting = JSON.parse(sortParam);
            }
        } catch (e) {
            console.warn('Failed to parse sort params', e);
        }

        let columnFilters = [];
        try {
            const filtersParam = searchParams.get('filters');
            if (filtersParam) {
                columnFilters = JSON.parse(filtersParam);
            }
        } catch (e) {
            console.warn('Failed to parse filter params', e);
        }

        return {
            pagination: { pageIndex: page, pageSize: size },
            globalFilter: search,
            sorting,
            columnFilters,
        };
    }, [searchParams, initialState.pageSize]);

    // Local state to manage rapid updates before syncing to URL (debounce could be added here)
    // For now, we sync directly.

    const setPagination = (updater) => {
        const nextPagination = typeof updater === 'function' ? updater(parsedState.pagination) : updater;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            // URL is 1-indexed
            next.set('page', nextPagination.pageIndex + 1);
            next.set('limit', nextPagination.pageSize);
            return next;
        }, { replace: true });
    };

    const setSorting = (updater) => {
        const nextSorting = typeof updater === 'function' ? updater(parsedState.sorting) : updater;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (nextSorting && nextSorting.length > 0) {
                next.set('sort', JSON.stringify(nextSorting));
            } else {
                next.delete('sort');
            }
            return next;
        }, { replace: true });
    };

    const setGlobalFilter = (updater) => {
        const nextFilter = typeof updater === 'function' ? updater(parsedState.globalFilter) : updater;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (nextFilter) {
                next.set('search', nextFilter);
            } else {
                next.delete('search');
            }
            // Reset page to 1 on search change
            next.set('page', 1);
            return next;
        }, { replace: true });
    };

    const setColumnFilters = (updater) => {
        const nextFilters = typeof updater === 'function' ? updater(parsedState.columnFilters) : updater;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (nextFilters && nextFilters.length > 0) {
                next.set('filters', JSON.stringify(nextFilters));
            } else {
                next.delete('filters');
            }
            // Reset page to 1 on filter change
            next.set('page', 1);
            return next;
        }, { replace: true });
    };

    /** Single URL write — avoids losing `filters` when batching two setSearchParams calls (e.g. Apply). */
    const applyFiltersAndSearch = useCallback((nextColumnFilters, nextSearch) => {
        const filters = typeof nextColumnFilters === 'function'
            ? nextColumnFilters(parsedState.columnFilters)
            : nextColumnFilters;
        const search = typeof nextSearch === 'function'
            ? nextSearch(parsedState.globalFilter)
            : nextSearch;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (filters && filters.length > 0) {
                next.set('filters', JSON.stringify(filters));
            } else {
                next.delete('filters');
            }
            if (search) {
                next.set('search', search);
            } else {
                next.delete('search');
            }
            next.set('page', '1');
            return next;
        }, { replace: true });
    }, [setSearchParams, parsedState.columnFilters, parsedState.globalFilter]);

    return {
        pagination: parsedState.pagination,
        sorting: parsedState.sorting,
        globalFilter: parsedState.globalFilter,
        columnFilters: parsedState.columnFilters,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        applyFiltersAndSearch,
    };
};

export default useTableUrlState;
